// The only browser state a form set keeps (spec §4.8, D9): per card, the
// request key and when it was sent — no DTO, no CPF, no name. The key name
// carries the tenant, the actor and the card, which is all the receipt-by-key
// read needs. The sessionStorage marker tells a reload of the tab that sent it
// (reuse the key) from another session (recovery blocked). Every guard is
// dropped on identity invalidation.
import { APP_CONSTANTS } from "@/config";
import type { keyAfter } from "@/services/copilot-form-sets";
import { useAuthStore } from "@/stores/use-auth-store";

const PREFIX = "copilot-form-set:";

export type SetGuard = { request_key: string; sent_at: string };
export type GuardRead = SetGuard | "unreadable" | null;

function tabKey(key: string): string {
  return `${key}:tab`;
}

function tryRemove(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Blocked storage holds nothing this module could have written.
  }
}

export function setGuardKey(tenant: string | null, userId: string | number | null | undefined, cardId: string): string {
  return `${PREFIX}${tenant ?? ""}:${userId ?? ""}:${cardId}`;
}

/**
 * Blocked storage reads as no guard: nothing could have been written there,
 * and `writeGuard` will refuse before any send. A record that exists but
 * cannot be parsed still blocks: a lost key must never become permission for
 * a new write.
 */
export function readGuard(key: string): GuardRead {
  let raw: string | null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const value = JSON.parse(raw) as Partial<SetGuard> | null;
    return value && typeof value.request_key === "string" && value.request_key && typeof value.sent_at === "string"
      ? { request_key: value.request_key, sent_at: value.sent_at }
      : "unreadable";
  } catch {
    return "unreadable";
  }
}

/**
 * Throws when storage is unavailable; the caller then does not send (persist
 * before HTTP). The tab marker goes first: a marker left alone blocks nothing,
 * while a record without its marker would read as another session's send.
 */
export function writeGuard(key: string, requestKey: string, now: Date = new Date()): SetGuard {
  const guard = { request_key: requestKey, sent_at: now.toISOString() };
  sessionStorage.setItem(tabKey(key), requestKey);
  localStorage.setItem(key, JSON.stringify(guard));
  return guard;
}

export function ownsGuard(key: string, guard: SetGuard): boolean {
  try {
    return sessionStorage.getItem(tabKey(key)) === guard.request_key;
  } catch {
    return false;
  }
}

export function clearGuard(key: string): void {
  tryRemove(localStorage, key);
  tryRemove(sessionStorage, tabKey(key));
}

/**
 * Applies `keyAfter(outcome)` — the service owns the key lifecycle: the guard is
 * kept for `reuse` and `read` (the key may own a committed set: it is resent or
 * its receipt read, never replaced), dropped for `fresh` (the next click mints
 * and guards a new key) and for `none` (terminal). Takes the decision, not the outcome, so this
 * module, imported at boot, does not pull the form-set service into the
 * initial bundle.
 */
export function settleGuard(key: string, next: ReturnType<typeof keyAfter>): void {
  if (next === "fresh" || next === "none") clearGuard(key);
}

function guardKeysIn(storage: Storage): string[] {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key?.startsWith(PREFIX)) keys.push(key);
  }
  return keys;
}

export function clearAllSetGuards(): void {
  for (const storage of [localStorage, sessionStorage]) {
    let keys: string[];
    try {
      keys = guardKeysIn(storage);
    } catch {
      continue;
    }
    for (const key of keys) tryRemove(storage, key);
  }
}

useAuthStore.subscribe((next, previous) => {
  const signedOut = next.sessionStatus === "signed-out" && previous.sessionStatus !== "signed-out";
  const switched = Boolean(previous.user)
    && (next.user?.id !== previous.user?.id || next.user?.tenant_id !== previous.user?.tenant_id);
  if (signedOut || switched) clearAllSetGuards();
});

function storedUserId(raw: string | null): unknown {
  if (!raw) return null;
  try {
    return (JSON.parse(raw) as { id?: unknown } | null)?.id ?? null;
  } catch {
    return null;
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.storageArea && event.storageArea !== localStorage) return;
    const tenantChanged = event.key === APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY && event.oldValue !== event.newValue;
    const userChanged = event.key === APP_CONSTANTS.AUTH_USER_LOCAL_STORAGE_KEY && storedUserId(event.oldValue) !== storedUserId(event.newValue);
    if (event.key === null || tenantChanged || userChanged) clearAllSetGuards();
  });
}
